import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, ExternalLink, FileText, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

type ContractType = "Fixed Term" | "Full Time" | "Limited Company";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onGenerated: () => Promise<void> | void;
  onQueued?: (attempt: { attemptId: string; personName?: string; contractType?: ContractType }) => void;
  onViewDocuments: () => void;
  person: any;
  profile: any;
}

interface GenerationResult {
  success?: boolean;
  generationStatus: "queued" | "success" | "partial_failure" | "failed";
  error?: string;
  warnings?: string[];
  configWarnings?: string[];
  personName: string;
  contractType: ContractType;
  generatedDate: string;
  attemptId?: string;
  retryAvailable?: boolean;
  contractRecord?: {
    id: string;
  };
  actions?: {
    contractUrl?: string | null;
    offerLetterUrl?: string | null;
    folderUrl?: string | null;
  };
}

interface ContractFormState {
  firstName: string;
  lastName: string;
  username: string;
  department: string;
  jobTitle: string;
  linkedInProfile: string;
  personalEmail: string;
  phoneNumber: string;
  homeAddress: string;
  addressLine2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
  startDate: string;
  endDate: string;
  salary: string;
  offerLetterDate: string;
  contractType: ContractType;
  companyName: string;
  annualLeaveAllowance: string;
}

const CONTRACT_TYPES: ContractType[] = ["Fixed Term", "Full Time", "Limited Company"];
const DEPARTMENT_OPTIONS = [
  "Animation",
  "Asset Build",
  "Comp",
  "Editorial",
  "FX",
  "gen",
  "Graphics",
  "Groom",
  "Layout",
  "Lighting",
  "Matchmove",
  "Matte Painting",
  "Pipeline and Support",
  "Production",
  "Supervision",
  "Vendor",
] as const;

const COUNTRY_OPTIONS = [
  { code: "AE", name: "United Arab Emirates", flag: "🇦🇪", dialCode: "+971" },
  { code: "AU", name: "Australia", flag: "🇦🇺", dialCode: "+61" },
  { code: "CA", name: "Canada", flag: "🇨🇦", dialCode: "+1" },
  { code: "DE", name: "Germany", flag: "🇩🇪", dialCode: "+49" },
  { code: "ES", name: "Spain", flag: "🇪🇸", dialCode: "+34" },
  { code: "FR", name: "France", flag: "🇫🇷", dialCode: "+33" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧", dialCode: "+44" },
  { code: "IE", name: "Ireland", flag: "🇮🇪", dialCode: "+353" },
  { code: "IN", name: "India", flag: "🇮🇳", dialCode: "+91" },
  { code: "IT", name: "Italy", flag: "🇮🇹", dialCode: "+39" },
  { code: "JP", name: "Japan", flag: "🇯🇵", dialCode: "+81" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱", dialCode: "+31" },
  { code: "NZ", name: "New Zealand", flag: "🇳🇿", dialCode: "+64" },
  { code: "SE", name: "Sweden", flag: "🇸🇪", dialCode: "+46" },
  { code: "US", name: "United States", flag: "🇺🇸", dialCode: "+1" },
] as const;

const UK_POSTCODE_PATTERN =
  /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}|GIR ?0AA)$/i;

function normalizeDialCode(value: string) {
  const trimmed = value.trim();
  if (!trimmed.startsWith("+")) return "";
  return trimmed.replace(/[^\d+]/g, "");
}

function detectCountryFromPhoneNumber(phoneNumber: string) {
  const normalized = normalizeDialCode(phoneNumber);
  if (!normalized) return null;

  const matches = COUNTRY_OPTIONS.filter((country) => normalized.startsWith(country.dialCode)).sort(
    (left, right) => right.dialCode.length - left.dialCode.length,
  );
  return matches[0] || null;
}

function findCountryOptionByName(name: string) {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return null;
  return COUNTRY_OPTIONS.find((country) => country.name.toLowerCase() === normalized) || null;
}

function splitName(name?: string) {
  const trimmed = (name || "").trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function buildInitialState(person: any, profile: any): ContractFormState {
  const { firstName, lastName } = splitName(person?.name);
  const initialContractType = CONTRACT_TYPES.includes(profile?.contractType) ? profile.contractType : "Full Time";
  return {
    firstName,
    lastName,
    username: profile?.username || (person?.email ? String(person.email).split("@")[0] : ""),
    department: profile?.department || person?.department || "",
    jobTitle: profile?.jobTitle || person?.title || "",
    linkedInProfile: profile?.linkedInProfile || "",
    personalEmail: profile?.personalEmail || person?.email || "",
    phoneNumber: profile?.personalPhone || profile?.phone || "",
    homeAddress: profile?.address || "",
    addressLine2: profile?.addressLine2 || "",
    city: profile?.city || "",
    stateProvince: profile?.stateProvince || "",
    postalCode: profile?.postalCode || "",
    country: profile?.country || "",
    startDate: profile?.contractStartDate || "",
    endDate: profile?.contractEndDate || "",
    salary:
      initialContractType === "Limited Company"
        ? profile?.dailyRate != null
          ? String(profile.dailyRate)
          : ""
        : profile?.salary != null
          ? String(profile.salary)
          : "",
    offerLetterDate: profile?.offerLetterDate || new Date().toISOString().slice(0, 10),
    contractType: initialContractType,
    companyName: profile?.companyName || "",
    annualLeaveAllowance:
      profile?.annualLeaveAllowance != null
        ? String(profile.annualLeaveAllowance)
        : profile?.leaveAllowance != null
          ? String(profile.leaveAllowance)
          : "",
  };
}

function formatGenerationTimestamp(value?: string) {
  if (!value) return "Attempt failed before documents were created.";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Attempt failed before documents were created.";
  }
  return parsed.toLocaleString();
}

export function PersonContractGenerationModal({
  isOpen,
  onClose,
  onGenerated,
  onQueued,
  onViewDocuments,
  person,
  profile,
}: Props) {
  const { user } = useAuth();
  const [form, setForm] = useState<ContractFormState>(() => buildInitialState(person, profile));
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<GenerationResult | null>(null);
  const lastAutoDetectedCountry = useRef<string>("");

  useEffect(() => {
    if (!isOpen) return;
    setForm(buildInitialState(person, profile));
    setAttachment(null);
    setSubmitting(false);
    setResult(null);
    lastAutoDetectedCountry.current = "";
  }, [isOpen, person, profile]);

  const detectedPhoneCountry = useMemo(() => detectCountryFromPhoneNumber(form.phoneNumber), [form.phoneNumber]);
  const matchingCountryOption = useMemo(() => findCountryOptionByName(form.country), [form.country]);
  const filteredCountryOptions = useMemo(() => {
    const query = form.country.trim().toLowerCase();
    if (!query) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter((country) => country.name.toLowerCase().includes(query)).slice(0, 6);
  }, [form.country]);
  const postcodeLooksUk = useMemo(() => UK_POSTCODE_PATTERN.test(form.postalCode.trim()), [form.postalCode]);

  useEffect(() => {
    if (!detectedPhoneCountry) return;
    const currentCountry = form.country.trim();
    const previousAutoDetectedCountry = lastAutoDetectedCountry.current;
    if (currentCountry && currentCountry !== previousAutoDetectedCountry) return;
    if (currentCountry === detectedPhoneCountry.name) return;

    lastAutoDetectedCountry.current = detectedPhoneCountry.name;
    setForm((current) => {
      if (
        current.country.trim() &&
        current.country.trim() !== previousAutoDetectedCountry &&
        current.country.trim() !== detectedPhoneCountry.name
      ) {
        return current;
      }
      return {
        ...current,
        country: detectedPhoneCountry.name,
      };
    });
  }, [detectedPhoneCountry, form.country]);

  useEffect(() => {
    if (!postcodeLooksUk || form.country.trim()) return;
    setForm((current) => {
      if (current.country.trim()) return current;
      return {
        ...current,
        country: "United Kingdom",
      };
    });
  }, [postcodeLooksUk, form.country]);

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!form.firstName.trim()) errors.push("First name");
    if (!form.lastName.trim()) errors.push("Surname");
    if (!form.username.trim()) errors.push("Username");
    if (!form.department.trim()) errors.push("Department");
    if (!form.jobTitle.trim()) errors.push("Job title");
    if (!form.personalEmail.trim()) errors.push("Personal email");
    if (!form.phoneNumber.trim()) errors.push("Phone number");
    if (!form.homeAddress.trim()) errors.push("Home address");
    if (!form.startDate) errors.push("Start date");
    if (!form.salary.trim()) errors.push(form.contractType === "Limited Company" ? "Daily rate" : "Salary");
    if (!form.offerLetterDate) errors.push("Offer letter date");
    if (!form.contractType) errors.push("Contract type");
    return errors;
  }, [form]);

  if (!isOpen) return null;

  const hasRecoverableResult = Boolean(result?.contractRecord || result?.actions?.folderUrl);
  const compensationLabel = form.contractType === "Limited Company" ? "Daily Rate" : "Salary";
  const isFailedResult = result?.generationStatus === "failed";
  const isPartialResult = result?.generationStatus === "partial_failure";
  const resultTitle = isFailedResult
    ? result?.error?.includes("already exists")
      ? "Contract already exists"
      : "Generation needs attention"
    : isPartialResult
      ? "Contract generated with warnings"
      : "Contract generated";
  const resultToneClasses = isFailedResult
    ? "border-rose-200 bg-rose-50 dark:border-rose-900/60 dark:bg-rose-950/30"
    : isPartialResult
      ? "border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30"
      : "border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30";
  const resultIconClasses = isFailedResult
    ? "text-rose-600 dark:text-rose-300"
    : isPartialResult
      ? "text-amber-600 dark:text-amber-300"
      : "text-emerald-600 dark:text-emerald-300";

  const updateField = <K extends keyof ContractFormState>(key: K, value: ContractFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) {
      toast.error("You must be signed in to generate contracts.");
      return;
    }
    if (validationErrors.length > 0) {
      toast.error(`Please complete the required fields: ${validationErrors.join(", ")}`);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value ?? "");
      });
      if (attachment) {
        formData.append("profileAsset", attachment);
      }

      const response = await fetch(`/api/people/profiles/${person.id}/contracts/generate`, {
        method: "POST",
        headers: {
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
        body: formData,
      });

      const data = (await response.json().catch(() => ({}))) as GenerationResult & { error?: string };
      if (response.status === 202 && data.generationStatus === "queued" && data.attemptId) {
        onQueued?.({
          attemptId: data.attemptId,
          personName: data.personName,
          contractType: data.contractType,
        });
        toast.success("Contract generation queued. We’ll let you know when it’s finished.");
        return;
      }
      if (!response.ok) {
        if (data.generationStatus) {
          setResult(data);
          if (data.contractRecord || data.actions?.folderUrl) {
            await onGenerated();
          }
          toast.error(data.error || "Contract generation needs attention.");
          return;
        }
        throw new Error(data.error || "Failed to generate contract.");
      }

      setResult(data);
      if (data.generationStatus === "partial_failure") {
        toast.warning("Contract generated with some follow-up issues. Review the warnings below.");
      } else {
        toast.success("Contract generated");
      }
      await onGenerated();
    } catch (error: any) {
      toast.error(error.message || "Failed to generate contract.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderTextInput = (
    label: string,
    key: keyof ContractFormState,
    required = false,
    options?: React.InputHTMLAttributes<HTMLInputElement>,
  ) => (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      <input
        {...options}
        value={form[key]}
        onChange={(e) => updateField(key, e.target.value as ContractFormState[typeof key])}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
      />
    </div>
  );

  const renderSelectInput = <K extends keyof ContractFormState>(
    label: string,
    key: K,
    values: readonly string[],
    required = false,
    placeholder = "Select option",
  ) => (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      <select
        value={form[key]}
        onChange={(event) => updateField(key, event.target.value as ContractFormState[K])}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
      >
        <option value="">{placeholder}</option>
        {values.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Generate Contract</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Create contract documents for {person?.name || "this person"} using the current HR record as the starting point.
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div className="space-y-6 overflow-y-auto px-6 py-6">
            <div className={`rounded-xl border p-4 ${resultToneClasses}`}>
              <div className="flex items-start gap-3">
                <FileText className={`mt-0.5 h-5 w-5 ${resultIconClasses}`} />
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {resultTitle}
                  </h3>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {[result.personName, result.contractType, formatGenerationTimestamp(result.generatedDate)]
                      .filter(Boolean)
                      .join(" • ")}
                  </p>
                  {result.attemptId ? (
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Attempt ID: {result.attemptId}</p>
                  ) : null}
                </div>
              </div>
            </div>

            {result.error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900/60 dark:bg-rose-950/30">
                <h3 className="text-sm font-semibold text-rose-900 dark:text-rose-100">Issue</h3>
                <p className="mt-2 text-sm text-rose-800 dark:text-rose-200">{result.error}</p>
              </div>
            ) : null}

            {result.warnings && result.warnings.length > 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30">
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Warnings</h3>
                <ul className="mt-2 space-y-1 text-sm text-amber-800 dark:text-amber-200">
                  {result.warnings.map((warning) => (
                    <li key={warning}>• {warning}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {result.configWarnings && result.configWarnings.length > 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Configuration notes</h3>
                <ul className="mt-2 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                  {result.configWarnings.map((warning) => (
                    <li key={warning}>• {warning}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3">
              {result.actions?.contractUrl ? (
                <a
                  href={result.actions.contractUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Contract
                </a>
              ) : null}
              {result.actions?.offerLetterUrl ? (
                <a
                  href={result.actions.offerLetterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Offer Letter
                </a>
              ) : null}
              {result.actions?.folderUrl ? (
                <a
                  href={result.actions.folderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Folder
                </a>
              ) : null}
              {(result.retryAvailable || isFailedResult) ? (
                <button
                  type="button"
                  onClick={() => setResult(null)}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FileText className="h-4 w-4" />
                  Back to Form
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  onViewDocuments();
                  onClose();
                }}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <Calendar className="h-4 w-4" />
                View Documents
              </button>
            </div>
            {!hasRecoverableResult && isFailedResult ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Your form values are still preserved. Use <span className="font-medium">Back to Form</span> to correct anything and retry.
              </p>
            ) : null}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-y-auto lg:grid-cols-2">
              <div className="space-y-4 border-b border-slate-200 px-6 py-6 dark:border-slate-800 lg:border-b-0 lg:border-r">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Personal Details</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput("First name", "firstName", true)}
                  {renderTextInput("Surname", "lastName", true)}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput("Username", "username", true)}
                  {renderSelectInput("Department", "department", DEPARTMENT_OPTIONS, true, "Select department")}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput("Job Title", "jobTitle", true)}
                  {renderTextInput("LinkedIn Profile", "linkedInProfile")}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput("Personal Email", "personalEmail", true, { type: "email" })}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                      Phone number <span className="text-rose-500">*</span>
                    </label>
                    <input
                      value={form.phoneNumber}
                      onChange={(event) => updateField("phoneNumber", event.target.value)}
                      placeholder="+44 7700 900123"
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {detectedPhoneCountry
                        ? `Dial code detected: ${detectedPhoneCountry.flag} ${detectedPhoneCountry.name}`
                        : "Use international format where possible so we can infer the country automatically."}
                    </p>
                  </div>
                </div>
                {renderTextInput("Home Address", "homeAddress", true)}
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput("Address Line 2", "addressLine2")}
                  {renderTextInput("City", "city")}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput("State / Province", "stateProvince")}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">ZIP / Postal code</label>
                    <input
                      value={form.postalCode}
                      onChange={(event) => updateField("postalCode", event.target.value)}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {postcodeLooksUk
                        ? "This looks like a UK postcode. We can add postcode-to-address lookup next once we choose a lookup service."
                        : "We can wire postcode lookup here later if we connect a UK address service."}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Country</label>
                    <input
                      value={form.country}
                      onChange={(event) => updateField("country", event.target.value)}
                      list="contract-country-options"
                      placeholder="Start typing a country"
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                    <datalist id="contract-country-options">
                      {COUNTRY_OPTIONS.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.flag} {country.name}
                        </option>
                      ))}
                    </datalist>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {matchingCountryOption
                        ? `${matchingCountryOption.flag} ${matchingCountryOption.name}`
                        : filteredCountryOptions.length > 0
                          ? `Suggestions: ${filteredCountryOptions.map((country) => country.name).join(", ")}`
                          : "Choose a country from the standard list to keep addresses clean and consistent."}
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Upload a file / profile image</label>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 transition hover:border-indigo-400 hover:text-indigo-600 dark:border-slate-700 dark:text-slate-300 dark:hover:border-indigo-500 dark:hover:text-indigo-300">
                      <Upload className="h-4 w-4" />
                      <span className="truncate">{attachment ? attachment.name : "Choose file"}</span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={(event) => setAttachment(event.target.files?.[0] || null)}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="space-y-4 px-6 py-6">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Contract Details</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput("Start date", "startDate", true, { type: "date" })}
                  {renderTextInput("End date", "endDate", false, { type: "date" })}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput(compensationLabel, "salary", true, { type: "number", min: "0", step: "1" })}
                  {renderTextInput("Offer Letter date", "offerLetterDate", true, { type: "date" })}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                      Contract Type <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={form.contractType}
                      onChange={(e) => updateField("contractType", e.target.value as ContractType)}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    >
                      {CONTRACT_TYPES.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  {renderTextInput("Company Name", "companyName")}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput("Annual Leave Allowance", "annualLeaveAllowance", false, { type: "number", min: "0", step: "1" })}
                </div>

                {validationErrors.length > 0 ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                    Required before generation: {validationErrors.join(", ")}
                  </div>
                ) : null}

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
                  The generated files will be placed in:
                  <div className="mt-2 font-medium text-slate-900 dark:text-slate-100">
                    {person?.name || "Person"} &gt; Contracts &gt; {(form.offerLetterDate || form.startDate || "YYYY-MM-DD")} - {form.contractType} Contract
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-800">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                Generate Contract
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
